import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmActionModalComponent } from '../confirm-action-modal/confirm-action-modal.component';
import { PauseDates, PauseErrorCode } from '../../types/subscription-pause.types';
import {
  MAX_PAUSE_DAYS,
  PAUSE_MONTH_DAYS,
  isSameDay,
  pauseLengthDays,
  pauseLengthError,
  pauseWindow,
  pauseWindowThrough,
  projectedNextRenewal,
  stepPauseDays,
  todayIn,
  toYmd,
} from '../../utils/subscription-pause.util';
import {
  formatPauseDate,
  pauseConfirmLabel,
  pauseLengthLabel,
  pauseRefusalCopy,
} from '../../utils/subscription-pause.copy';

/** How far ahead a pause can be scheduled in the calendar. */
const MAX_MONTHS_AHEAD = 11;

export interface PauseCalendarDay {
  date: Date;
  label: number;
  disabled: boolean;
  isStart: boolean;
  isEnd: boolean;
  inRange: boolean;
}

export interface PauseCalendarMonth {
  title: string;
  /** Rows of seven; null pads the days outside the month. */
  weeks: (PauseCalendarDay | null)[][];
}

/**
 * "Pause Subscription": pick a length with the 1-4 month stepper (30 days each) or choose the
 * start and last paused day on a two-month calendar (7-120 days), then confirm. Presentational: it makes no API call. The host runs the pause (see
 * PauseFlowController) and feeds `busy` and `errorCode` back. Shared by the Customers →
 * Subscriptions tab and the lane subscription popups.
 *
 * @selector app-pause-subscription-modal
 * @input visible - Renders the modal; the selection resets each time it opens.
 * @input periodEnd - End of the paid period, to project the next renewal after the pause.
 * @input timeZone - The site's IANA zone; "today" and the projection follow it (the browser's when missing).
 * @input busy - Request in flight: controls are disabled.
 * @input errorCode - Refusal to show inside the modal.
 * @output confirm - `{ start, resume_date }` as local `YYYY-MM-DD`.
 * @output cancel - Closed without pausing.
 * @output selectionChange - Months or start changed; the host clears its `errorCode`.
 */
@Component({
  selector: 'app-pause-subscription-modal',
  standalone: true,
  imports: [CommonModule, ConfirmActionModalComponent],
  templateUrl: './pause-subscription-modal.component.html',
  styleUrls: ['./pause-subscription-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PauseSubscriptionModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() periodEnd: string | null = null;
  @Input() timeZone: string | null = null;
  @Input() busy = false;
  @Input() errorCode: PauseErrorCode | null = null;

  @Output() confirm = new EventEmitter<PauseDates>();
  @Output() cancel = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<void>();

  readonly weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  start: Date = todayIn();
  /** Last day of the pause; access resumes the day after. */
  lastPausedDay: Date = pauseWindow(this.start, 1).lastPausedDay;
  /** Which end the next calendar click sets: a click on a new start begins a new range. */
  picking: 'start' | 'end' = 'start';
  /** First day of the month shown in the left calendar. */
  viewMonth: Date = new Date(this.start.getFullYear(), this.start.getMonth(), 1);

  calendars: PauseCalendarMonth[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.reset();
    }
  }

  get window() {
    return pauseWindowThrough(this.start, this.lastPausedDay);
  }

  get lengthDays(): number {
    return pauseLengthDays(this.window);
  }

  /** "1 month" for a whole number of 30-day months, otherwise "10 days". */
  get lengthLabel(): string {
    return pauseLengthLabel(this.lengthDays);
  }

  get confirmLabel(): string {
    return pauseConfirmLabel(this.lengthDays);
  }

  /** A length outside 7-120 days: shown inline, and Confirm stays disabled. */
  get rangeError(): 'TOO_SHORT' | 'TOO_LONG' | null {
    return pauseLengthError(this.lengthDays);
  }

  get canStepDown(): boolean {
    return !this.busy && this.lengthDays > PAUSE_MONTH_DAYS;
  }

  get canStepUp(): boolean {
    return !this.busy && this.lengthDays < MAX_PAUSE_DAYS;
  }

  get startsToday(): boolean {
    return isSameDay(this.start, this.today);
  }

  get startLabel(): string {
    return this.startsToday ? 'Today' : formatPauseDate(this.start);
  }

  get resumesOnLabel(): string {
    return formatPauseDate(this.window.resumeDate);
  }

  get nextRenewalLabel(): string {
    const renewal = projectedNextRenewal(
      this.periodEnd,
      this.start,
      this.window.resumeDate,
      this.timeZone
    );
    return renewal ? formatPauseDate(renewal) : '—';
  }

  /** A refusal from the API wins over the local length check. */
  get errorText(): string {
    return pauseRefusalCopy(this.errorCode ?? this.rangeError);
  }

  get canGoBack(): boolean {
    return this.viewMonth.getTime() > this.firstOfMonth(this.today).getTime();
  }

  get canGoForward(): boolean {
    const limit = new Date(this.today.getFullYear(), this.today.getMonth() + MAX_MONTHS_AHEAD, 1);
    return this.viewMonth.getTime() < limit.getTime();
  }

  private get today(): Date {
    return todayIn(this.timeZone);
  }

  /** Quick pick: the next whole number of 30-day months above or below the current length. */
  stepLength(direction: 1 | -1): void {
    if (this.busy || (direction === 1 ? !this.canStepUp : !this.canStepDown)) {
      return;
    }
    const days = stepPauseDays(this.lengthDays, direction);
    this.lastPausedDay = pauseWindow(this.start, days / PAUSE_MONTH_DAYS).lastPausedDay;
    this.picking = 'start';
    this.selectionChange.emit();
    this.buildCalendars();
  }

  /** First click begins a range at that day (30-day default end); the next later day ends it. */
  pickDay(day: PauseCalendarDay): void {
    if (day.disabled || this.busy) {
      return;
    }
    if (this.picking === 'end' && day.date > this.start) {
      this.lastPausedDay = day.date;
      this.picking = 'start';
    } else {
      this.start = day.date;
      this.lastPausedDay = pauseWindow(this.start, 1).lastPausedDay;
      this.picking = 'end';
    }
    this.selectionChange.emit();
    this.buildCalendars();
  }

  navigate(delta: number): void {
    if ((delta < 0 && !this.canGoBack) || (delta > 0 && !this.canGoForward)) {
      return;
    }
    this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + delta, 1);
    this.buildCalendars();
  }

  onConfirm(): void {
    if (this.rangeError || this.busy) {
      return;
    }
    const { start, resumeDate } = this.window;
    this.confirm.emit({ start: toYmd(start), resume_date: toYmd(resumeDate) });
  }

  private reset(): void {
    this.start = this.today;
    this.lastPausedDay = pauseWindow(this.start, 1).lastPausedDay;
    this.picking = 'start';
    this.viewMonth = this.firstOfMonth(this.start);
    this.buildCalendars();
  }

  private firstOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private buildCalendars(): void {
    const { start, lastPausedDay } = this.window;
    const today = this.today;
    this.calendars = [0, 1].map(offset => {
      const first = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + offset, 1);
      const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
      const cells: (PauseCalendarDay | null)[] = Array.from({ length: first.getDay() }, () => null);
      for (let label = 1; label <= daysInMonth; label += 1) {
        const date = new Date(first.getFullYear(), first.getMonth(), label);
        cells.push({
          date,
          label,
          disabled: date < today,
          isStart: isSameDay(date, start),
          isEnd: isSameDay(date, lastPausedDay),
          inRange: date >= start && date <= lastPausedDay,
        });
      }
      while (cells.length % 7 !== 0) {
        cells.push(null);
      }
      const weeks: (PauseCalendarDay | null)[][] = [];
      for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
      }
      return {
        title: first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        weeks,
      };
    });
  }
}
